/**
 * Formatting templates (decision 78; part 15e log, table TL): the library the Formatting page lists, and what a change
 * to it writes. Pure: the workspace reads the records and hands the ops to the store as one transaction.
 *
 * A book's own theme record stays what every output reads (principle 1): the link only says which books a change to a
 * template rewrites, so a template and its books change together or not at all (principle 2).
 */
import type { BookRecord, StoreOp, TemplateRecord } from '@cockatrice/storage';
import { MAX_TEMPLATE_NAME } from '@cockatrice/storage';
import { serializeTheme, THEME_PRESETS, themeFromJson } from '@cockatrice/layout';
import type { PresetId, Theme } from '@cockatrice/layout';

/** The built-in templates, in the order the page lists them (TL2, TL10). */
export const BUILT_IN_TEMPLATES: readonly PresetId[] = Object.freeze(['classic', 'elegant', 'modern', 'large-print']);

/** Whether a link names a built-in template. */
export function isBuiltIn(link: string): link is PresetId {
  return BUILT_IN_TEMPLATES.some((id) => id === link);
}

/** A template as the page shows it. */
export interface TemplateView {
  readonly id: string;
  readonly name: string;
  readonly theme: Theme;
  readonly builtIn: boolean;
  readonly favourite: boolean;
  /** When it was made; a built-in's place among them. */
  readonly createdAt: number;
}

/** The writer's templates as views; one whose theme can't be read is left out of the list, and its record kept (B3). */
export function ownTemplates(records: readonly TemplateRecord[]): TemplateView[] {
  return records.flatMap((record) => {
    try {
      return [{ id: record.id, name: record.name, theme: themeFromJson(record.theme), builtIn: false, favourite: record.favourite, createdAt: record.createdAt }];
    } catch {
      return [];
    }
  });
}

/** The built-in templates as views: `name` gives each its name; `favourites` are the ones starred on this device (TL11). */
export function builtInTemplates(name: (id: PresetId) => string, favourites: readonly string[]): TemplateView[] {
  return BUILT_IN_TEMPLATES.map((id, index) => ({ id, name: name(id), theme: THEME_PRESETS[id], builtIn: true, favourite: favourites.includes(id), createdAt: index }));
}

/** Decision 78: favourites first; among each kind the built-ins, then the writer's own, oldest first, ties by ID so every tab agrees. */
export function orderTemplates(templates: readonly TemplateView[]): TemplateView[] {
  const rank = (template: TemplateView): number => (template.favourite ? 0 : 2) + (template.builtIn ? 0 : 1);
  return [...templates].sort((a, b) => rank(a) - rank(b) || a.createdAt - b.createdAt || (a.id < b.id ? -1 : 1));
}

/**
 * The template the page marks as the book's: its link, or for a book with none (an older book) the one whose theme it
 * has, so a book on the default look shows on Classic; null when it matches none.
 */
export function shownTemplate(link: string | null, theme: Theme, templates: readonly TemplateView[]): string | null {
  if (link !== null) return link;
  const text = serializeTheme(theme);
  return templates.find((template) => serializeTheme(template.theme) === text)?.id ?? null;
}

/** A template's name as stored: one line, without space at its ends, cut to what the record holds. */
export function cleanTemplateName(name: string): string {
  return name.replaceAll(/\s+/gu, ' ').trim().slice(0, MAX_TEMPLATE_NAME);
}

/** The books `id` is the template of. */
export function linkedBooks(books: readonly BookRecord[], id: string): BookRecord[] {
  return books.filter((book) => book.template === id);
}

/** TL1: the ops that give `template` the theme `theme` and every book linked to it the same, for one transaction. */
export function writeThrough(template: TemplateRecord, theme: Theme, books: readonly BookRecord[]): StoreOp[] {
  const text = serializeTheme(theme);
  return [
    { kind: 'put', store: 'templates', value: { ...template, theme: text } },
    ...linkedBooks(books, template.id).map((book): StoreOp => ({ kind: 'put', store: 'books', value: { ...book, theme: text } })),
  ];
}

/** TL2, TL3, TL6, TL7: a new template of the writer's, unstarred. */
export function newTemplate(id: string, name: string, theme: Theme, now: number): TemplateRecord {
  return { id, name: cleanTemplateName(name), theme: serializeTheme(theme), favourite: false, createdAt: now };
}

/** TL5: `book` taking `theme` from the template `link`: its theme and link, nothing else of it. */
export function linkBook(book: BookRecord, link: string, theme: Theme): BookRecord {
  return { ...book, theme: serializeTheme(theme), template: link };
}

/** TL9: the ops that delete template `id`: the books on it keep their theme and lose the link, in the same transaction. */
export function deleteTemplate(id: string, books: readonly BookRecord[]): StoreOp[] {
  return [
    { kind: 'delete', store: 'templates', id },
    ...linkedBooks(books, id).map((book): StoreOp => ({ kind: 'put', store: 'books', value: { ...book, template: null } })),
  ];
}

/**
 * TL13, TL14: the books a read of the library finds out of step, as they should be: a book whose own template holds another theme takes it (a
 * write raced another tab), and a book whose template is gone is unlinked with its theme kept. A record that exists but
 * can't be read (`unreadable`) is no proof of either, so its books are left as they are; a built-in's theme is the
 * book's own to keep, so a later build's change to a preset never restyles a book.
 */
export function healLinks(books: readonly BookRecord[], templates: readonly TemplateRecord[], unreadable: ReadonlySet<string>): BookRecord[] {
  const healed: BookRecord[] = [];
  for (const book of books) {
    const link = book.template;
    if (link === null || isBuiltIn(link) || unreadable.has(link)) continue;
    const template = templates.find((each) => each.id === link);
    if (template === undefined) healed.push({ ...book, template: null });
    else if (template.theme !== book.theme) healed.push({ ...book, theme: template.theme });
  }
  return healed;
}
